<script setup>
import { usePageSeo } from "~/composables/usePageSeo";
import Docs from "../../components/Docs.vue";
import {
  brandConfigs,
  brandThemes,
  brandKeyByCodeName,
  brandKeyByPrefix,
} from "~/pages/brand/config";
import { computed } from "vue";

const route = useRoute();
const code = route.params.code;

usePageSeo({
  title: `Buy SSL Certificate at Cheap Price`,
  description:
    "Buy SSL certificates at cheap price from the world's largest SSL marketplace, 100% customer satisfaction, 30 days money back & 24/7 Support.",
  bodyId: "product-page",
});

// Fetch product detail from our server API

console.log(route.params.code);
// 获取商品参数详情
const { data: product } = await useFetch("/api/product-by-code", {
  query: { code: route.params.code },
  default: () => ({ status: 0, data: [] }),
});
// 获取商品价格

const { data: price } = await useFetch("/api/product-list-price", {
  query: { code: route.params.code },
  default: () => ({ status: 0, data: [] }),
});

const listPrice = price.value.data.list || 0;
console.log(32, listPrice);

const p = product.value.data[0];

// Update page title with product name
if (p.name) {
  useHead({ title: `Buy ${p.name} at Cheap Price` });
}

// State for pricing
const selectedYear = ref(1);
const quantity = ref(1);
const standardCount = ref(1);
const wildcardCount = ref(1);
const install = ref("1");

// Price calculation state
const totalPrice = computed(() => {
  return (currentPricing.value?.buyPrice * selectedYear.value).toFixed(2);
});
const totalDomains = ref("");
const standardUnitDisplay = ref("...");
const wildcardUnitDisplay = ref("...");

// Computed pricing for selected year
const currentPricing = computed(() => {
  return listPrice?.find((pr) => pr.yearCount === selectedYear.value);
});

const hasSANOptions = computed(
  () => p.supportStandardOption || p.supportWildOption,
);

const lowestPrice = computed(() => {
  if (!listPrice?.length) return p.listprice || 0;
  return Math.min(...listPrice.map((pr) => pr.buyPrice));
});

const validationType = computed(() => {
  const type = p.extras
    .find((e) => e.key === "ssl_validation_type")
    ?.value?.toUpperCase();
  return type + " SSL certificate";
});

// Format price
function fmt(val) {
  if (val == null || isNaN(val)) return "0.00";
  return Number(val)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Calculate discount rate
function rate(listprice, price) {
  if (!listprice || listprice === 0) return "NaN";
  return Math.round(((listprice - price) / listprice) * 100);
}

// Number input helpers
function ensureNumber(val, min = 1) {
  const n = Number(val);
  return isNaN(n) || n < min ? min : n;
}

function decrement(refVal) {
  if (refVal.value > 1) refVal.value--;
}
function increment(refVal) {
  refVal.value++;
}

// Brand theming
function luminance(hex) {
  const rgb = parseInt(String(hex).slice(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

const brandKey = computed(() => {
  return p.brand.toLowerCase() + "-ssl";
});

const theme = computed(
  () =>
    brandThemes[brandKey.value] || {
      bgColor: "#09283c",
      logo: p.brandImage || "",
    },
);
const bannerTextColor = computed(
  () =>
    theme.value.h1Color ||
    (luminance(theme.value.bgColor) > 0.5 ? "#0e1726" : "#fff"),
);

const brandProductCodes = computed(() => {
  return brandKey.value
    ? brandConfigs[brandKey.value]?.brandProducts || []
    : [];
});

// More brand products by fixed codes from config.ts
const { data: moreResp } = await useFetch("/api/product-by-code", {
  query: computed(() => ({ code: brandProductCodes.value.join(",") })),
  default: () => ({ status: 0, data: [] }),
});

const moreBrandProducts = computed(() => moreResp.value?.data || []);
const specs = computed(() => {
  const arr = [
    {
      key: "Brand",
      value: `/static/images/brand/${p.brand}.png`,
    },
    {
      key: "Validation type",
      value: p.extras
        .find((e) => e.key === "ssl_validation_type")
        ?.value.toUpperCase(),
    },
    {
      key: "Issuance time",
      value: p.feat2,
    },
    {
      key: "Domain included",
      value: p.extras.find((e) => e.key === "default_protect_domain")?.value,
    },
    {
      key: "Additional domain(s)",
      value: p.extras.find((e) => e.key === "default_protect_domain")?.value,
    },
    {
      key: "Reissuance",
      value: p.extras.find((e) => e.key === "reissue_term")?.value,
    },
    {
      key: "Encryption Level",
      value: p.extras.find((e) => e.key === "encryption_levels")?.value,
    },
    {
      key: "Available hash algorithm",
      value: p.feat4,
    },
    {
      key: "Mobile Compatibility",
      value: p.extras.find((e) => e.key === "mobile_device_compatibility")
        ?.value,
    },
    {
      key: "Browser Compatibility",
      value: p.extras.find((e) => e.key === "browser_compatibility")?.value,
    },

    {
      key: "Warranty",
      value: p.feat3,
    },

    {
      key: "Secure Seal",
      value: `/static/images/brand/${p.brand}-seal.png`,
    },
  ];
  return arr;
});
</script>

<template>
  <div>
    <!-- Banner -->
    <section
      class="product-banner product-banner--brand"
      style="background-color: #e7edff"
    >
      <div class="product-banner-inner">
        <h1 :style="{ color: bannerTextColor }">{{ p.name }}</h1>
        <p :style="{ color: bannerTextColor }">{{ p.description }}</p>
        <img
          v-if="theme.logo"
          :src="theme.logo"
          :alt="p.brand"
          class="product-banner-logo"
        />
      </div>
    </section>

    <div class="inner-bound pt60 pb60">
      <div class="product-pricing-header">
        <p class="product-lowest-price">
          Lowest price: <span>{{ p.currency }}{{ fmt(lowestPrice) }}/year</span>
        </p>
        <p class="product-market-price">
          Market price:
          <s>{{ p.currency }}{{ fmt(listPrice[0]?.marketPrice) }}/year</s>
        </p>
        <p class="product-pricing-hint">
          Order a multi-year SSL certificate and enjoy even greater discounts!
        </p>
      </div>

      <div class="product-buy">
        <div class="product-buy-left">
          <!-- Year pricing options -->
          <div class="product-left-box base-card">
            <label
              v-for="pricing in listPrice"
              :key="pricing.yearCount"
              :class="[
                'product-year-row',
                {
                  'product-year-row--active':
                    selectedYear === pricing.yearCount,
                },
              ]"
              @click="selectedYear = pricing.yearCount"
            >
              <div class="product-year-cont">
                <input
                  type="radio"
                  name="price"
                  :checked="selectedYear === pricing.yearCount"
                  :value="pricing.yearCount"
                  @change="selectedYear = pricing.yearCount"
                />

                <span class="product-year-label"
                  >{{ pricing.yearCount }} year</span
                >
                <span class="product-year-price"
                  >{{ p.currency }}{{ fmt(pricing.buyPrice) }} yr</span
                >
              </div>

              <span class="product-year-discount"
                >{{ pricing.discountRatio }} off</span
              >
              <span class="product-year-save"
                >Save {{ p.currency
                }}{{ fmt(pricing.marketPrice - pricing.buyPrice) }} per
                year</span
              >
            </label>
          </div>

          <!-- SAN / Quantity / Install options -->
          <div
            v-if="hasSANOptions || p.supportInstall"
            class="product-options-panel"
          >
            <div v-if="hasSANOptions" class="product-sans">
              <p>Additional Domains(SANs):</p>
              <div>
                <div v-if="p.supportStandardOption" class="right-box-select">
                  <p>
                    Standard:@<span>{{ standardUnitDisplay }}</span
                    >/yr
                  </p>
                  <div class="number">
                    <button
                      class="number-minus"
                      @click="decrement(standardCount)"
                    ></button>
                    <input
                      :value="standardCount"
                      @input="standardCount = ensureNumber($event.target.value)"
                    />
                    <button
                      class="number-add"
                      @click="increment(standardCount)"
                    ></button>
                  </div>
                </div>
                <div v-if="p.supportWildOption" class="right-box-select">
                  <p>
                    Wildcard:@<span>{{ wildcardUnitDisplay }}</span
                    >/yr
                  </p>
                  <div class="number">
                    <button
                      class="number-minus"
                      @click="decrement(wildcardCount)"
                    ></button>
                    <input
                      :value="wildcardCount"
                      @input="wildcardCount = ensureNumber($event.target.value)"
                    />
                    <button
                      class="number-add"
                      @click="increment(wildcardCount)"
                    ></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary / Buy -->
        <div class="product-buy-right product-summary-card base-card">
          <div class="summary-brand">
            <img v-if="theme.logo" :src="theme.logo" :alt="p.brand" />
          </div>
          <h3 class="summary-title">{{ p.name }}</h3>
          <div class="summary-info">
            <div><span>Certificate type:</span> {{ validationType }}</div>
            <div><span>Validation time:</span> {{ p.feat2 }}</div>
            <div><span>Guarantee amount:</span> {{ p.feat3 }}</div>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-cont">
            <div class="summary-price">
              <p class="summary-unit">
                every year
                <span
                  >{{ p.currency }}{{ fmt(currentPricing?.buyPrice || 0) }} yr</span
                >
              </p>
              <span class="product-year-discount"
                >{{ currentPricing?.discountRatio }} off</span
              >
              <p class="summary-total">
                Final price <span>{{ p.currency }}{{ totalPrice }}</span>
              </p>
            </div>
            <div>
              <a
                :href="`https://console.nicsrs.com/product/config/${p.code}`"
                target="_blank"
                class="btn"
                >Buy Now</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Spec Table -->
    <div class="inner-bound padding2-60">
      <h2>{{ p.name }}</h2>
      <table class="product-table">
        <tbody>
          <tr v-for="(spec, idx) in specs" :key="idx">
            <td>{{ spec.key }}</td>
            <td>
              <template
                v-if="
                  spec.value &&
                  (spec.value.startsWith('/') || spec.value.startsWith('http'))
                "
              >
                <img
                  :src="spec.value"
                  :alt="p.name"
                  :class="spec.key === 'Brand' ? 'brand' : 'seal'"
                />
              </template>
              <template v-else>{{ spec.value }}</template>
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <a
                :href="`https://console.nicsrs.com/product/config/${p.code}`"
                class="buy-button"
                target="_blank"
                >Buy Now</a
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- More SSL from brand -->
    <div v-if="moreBrandProducts.length" class="ssl-section--muted">
      <div class="inner-bound padding2-60">
        <h2>More SSL from {{ p.brand }}</h2>
        <div class="product-more-grid">
          <div
            v-for="product in moreBrandProducts"
            :key="product.code"
            class="product-more-card base-card"
          >
            <SslProductCard :product="product" />
          </div>
        </div>
      </div>
    </div>

    <!-- Popular Wildcard certificates -->
    <div v-if="p.typeProducts?.length" class="inner-bound padding-60">
      <h2>Popular Wildcard certificate</h2>
      <div class="popular-cert">
        <div v-for="(item, idx) in p.typeProducts" :key="idx">
          <div class="popular-cert-box">
            <div v-if="item.brandImage">
              <img
                :src="item.brandImage"
                :alt="item.name"
                class="popular-cert-img"
              />
            </div>
            <div class="popular-cert-title">
              <NuxtLink :to="item.link">{{ item.name }}</NuxtLink>
            </div>
            <div class="popular-cert-price">
              <div>
                <p>${{ fmt(item.price) }}/yr</p>
                <p>${{ fmt(item.listPrice) }}/yr</p>
              </div>
              <p>{{ rate(item.listPrice, item.price) }}% Off</p>
            </div>
            <a :href="item.buyUrl" target="_blank" rel="noopener noreferrer"
              >BUY NOW</a
            >
          </div>
          <div class="popular-cert-info">
            <p v-html="item.spec"></p>
          </div>
        </div>
      </div>
    </div>

    <Docs />
  </div>
</template>

<style scoped>
.product-banner--brand {
  width: 100%;
  height: auto;
  /* min-height: 3rem; */
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.5rem 0.2rem;
}
.product-banner-inner {
  max-width: 10rem;
}
.product-banner--brand h1 {
  font-size: 0.48rem;
  font-weight: bold;
  margin-bottom: 0.2rem;
}
.product-banner--brand p {
  font-size: 0.18rem;
  line-height: 0.28rem;
  margin-bottom: 0.3rem;
}
.product-banner-logo {
  height: 0.6rem;
  max-width: 2.5rem;
  object-fit: contain;
}

.product-pricing-header {
  margin-bottom: 0.2rem;
}
.product-lowest-price {
  font-size: 0.24rem;
  font-weight: bold;
  color: #0e1726;
  margin-bottom: 0.06rem;
}
.product-lowest-price span {
  color: #ed7131;
}
.product-market-price {
  font-size: 0.14rem;
  color: #94a3b8;
  margin-bottom: 0.06rem;
}
.product-pricing-hint {
  font-size: 0.14rem;
  color: #64748b;
}

.product-buy-left {
  flex-direction: column;
  gap: 0.2rem;
}
.product-left-box {
  width: 100%;
  background-color: var(--theme-bg-color);
}
.product-year-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.12rem;
  padding: 0.18rem 0.2rem;
  border-bottom: 1px solid #e2e8f0;
  /* border-radius: 8px; */
  /* background-color: #fff; */
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.1rem;
  & :last-child {
    margin-bottom: 0;
  }
}
.product-year-row:hover,
.product-year-row--active {
  border-color: var(--theme-color);
  /* background-color: #f7f8fc; */
}
.product-year-row input {
  width: 0.18rem;
  height: 0.18rem;
  flex-shrink: 0;
  margin-right: 0.08rem;
}
.product-year-cont {
  width: 40%;
  display: flex;
  gap: 0.2rem;
}
.product-year-label {
  font-size: 0.18rem;
  font-weight: 600;
  color: #0e1726;
  width: 0.9rem;
  flex-shrink: 0;
}
.product-year-price {
  font-size: 0.2rem;
  font-weight: 700;
  color: #ed7131;
  flex: 1;
}
.product-year-discount {
  background-color: var(--theme-color);
  color: #fff;
  font-size: 0.12rem;
  font-weight: 600;
  padding: 0.04rem 0.1rem;
  border-radius: 999px;
  flex-shrink: 0;
}
.product-year-save {
  font-size: 0.13rem;
  color: #64748b;
  flex-shrink: 0;
  /* margin-left: auto; */
}

.product-options-panel {
  width: 100%;
  background-color: #f7f8fc;
  padding: 0.24rem;
  border-radius: 8px;
}

.product-summary-card {
  background-color: var(--theme-bg-color);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.3rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
}
.summary-cont {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.summary-brand {
  height: 0.5rem;
  margin-bottom: 0.16rem;
}
.summary-brand img {
  height: 100%;
  max-width: 1.8rem;
  object-fit: contain;
}
.summary-title {
  font-size: 0.22rem;
  font-weight: 700;
  color: #0e1726;
  margin-bottom: 0.16rem;
}
.summary-info {
  width: 100%;
  font-size: 0.14rem;
  color: #475569;
  line-height: 0.26rem;
  margin-bottom: 0.16rem;
}
.summary-info span {
  margin-right: 0.08rem;
}
.summary-divider {
  width: 100%;
  height: 1px;
  background-color: #e2e8f0;
  margin: 0.1rem 0;
}
.summary-price {
  margin-bottom: 0.16rem;
}
.summary-unit {
  font-size: 0.14rem;
  color: #64748b;
  margin-bottom: 0.06rem;
}
.summary-unit span {
  color: #ed7131;
  font-weight: 700;
}

.summary-total {
  font-size: 0.14rem;
  color: #64748b;
}
.summary-total span {
  color: #ed7131;
  font-size: 0.24rem;
  font-weight: 700;
  margin-left: 0.08rem;
}
.summary-buy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.13rem 0;
  border: 1px solid v-bind("theme.bgColor");
  border-radius: 8px;
  color: v-bind("theme.bgColor");
  font-size: 0.16rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}
.summary-buy:hover {
  background-color: v-bind("theme.bgColor");
  color: #fff;
}

.product-more-section {
  background-color: #f7f8fc;
}
.product-more-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
}
.product-more-card {
  width: calc(25% - 0.15rem);
  padding: 0.28rem;
  background-color: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  transition: all 0.25s ease;
}
.product-more-card:hover {
  transform: translateY(-4px);
}

@media screen and (max-width: 1200px) {
  .product-more-card {
    width: calc(50% - 0.1rem);
  }
}
@media screen and (max-width: 640px) {
  .product-banner--brand h1 {
    font-size: 0.36rem;
  }
  .product-year-row {
    flex-wrap: wrap;
  }
  .product-year-save {
    width: 100%;
    margin-left: 0;
    margin-top: 0.06rem;
  }
  .product-buy-right {
    width: 100%;
    /* margin-top: 0.2rem; */
  }
}
</style>
